import { useQuery, TypedDocumentNode, WatchQueryFetchPolicy } from "@apollo/client";
import { Autocomplete, TextField, SxProps, Theme, TextFieldProps } from "@mui/material";
import { useDebounce } from "hooks/useDebounce";
import React, { useState, useMemo } from "react";
import { FieldError } from "react-hook-form";

import { ErrorDisplay } from "./Forms/ErrorDisplay";

interface OmnisearchAutocompleteProps<TData, TVariables, TItem extends { id: string }> {
  disabled?: boolean;
  dataQuery: TypedDocumentNode<TData, TVariables>;
  dataQueryVariables?: Omit<TVariables, "search">;
  getItems: (data: TData) => TItem[];
  placeholder?: string;
  onSelectionChange?: (item: TItem | null) => void;
  defaultSearch?: string;
  runInitialSearch?: boolean;
  renderOption?: (props: React.HTMLAttributes<HTMLLIElement>, item: TItem) => React.ReactNode;
  sx?: SxProps<Theme>;
  /**
   * Initial search query to populate the search input.
   * This allows setting a starting search term that can be overwritten by the user.
   * Useful for pre-filling the search based on external factors or user preferences.
   */
  initialSearchQuery?: string;

  // New form integration props
  /** Function to get display label for options */
  getOptionLabel?: (item: TItem) => string;
  /** Controlled value */
  value?: TItem | null;
  /** Change handler - consistent naming with form libraries */
  onChange?: (value: TItem | null) => void;
  /** Form validation error */
  error?: FieldError;
  /** Helper text to display */
  helperText?: string | React.JSX.Element;
  /** Function to filter results after query execution */
  resultFilter?: (item: TItem) => boolean;
  /** Function to disable specific options */
  getOptionDisabled?: (item: TItem) => boolean;
  /** Input label props */
  inputLabelProps?: TextFieldProps;
  hideError?: boolean;
  /** Apollo fetch policy - defaults to cache-and-network to ensure fresh data when tenant changes */
  fetchPolicy?: WatchQueryFetchPolicy;
  /** Apollo query context passed to useQuery (e.g. for tenant overrides) */
  queryContext?: Record<string, unknown>;
}

export function OmnisearchAutocomplete<TData, TVariables, TItem extends { id: string }>({
  disabled = false,
  dataQuery,
  dataQueryVariables,
  getItems,
  placeholder = "Search...",
  onSelectionChange,
  defaultSearch = "",
  renderOption,
  runInitialSearch = false,
  sx,
  initialSearchQuery = "",
  // New props with defaults
  getOptionLabel = (item: TItem) => item.id,
  value,
  onChange,
  error,
  helperText,
  resultFilter,
  getOptionDisabled,
  inputLabelProps,
  hideError = false,
  fetchPolicy = "cache-and-network",
  queryContext,
}: OmnisearchAutocompleteProps<TData, TVariables, TItem>) {
  const [search, setSearch] = useState(initialSearchQuery);
  const [debouncedSearch] = useDebounce(search, 300);

  const { data, loading } = useQuery(dataQuery, {
    variables: {
      search: [defaultSearch, debouncedSearch].filter(Boolean).join(" "),
      ...dataQueryVariables,
    },
    skip: !runInitialSearch && debouncedSearch.length < 2,
    fetchPolicy,
    context: queryContext,
  });

  const items = useMemo(() => {
    if (!data) return [];
    const baseItems = getItems(data);
    return resultFilter ? baseItems.filter(resultFilter) : baseItems;
  }, [data, getItems, resultFilter]);

  const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setSearch(newInputValue);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: TItem | null) => {
    // Support both new and legacy callback patterns
    if (onChange) {
      onChange(newValue);
    }
    if (onSelectionChange) {
      onSelectionChange(newValue);
    }
    setSearch(""); // Clear the input after selection
  };

  return (
    <>
      <Autocomplete<TItem>
        disabled={disabled}
        sx={sx}
        options={items}
        getOptionLabel={getOptionLabel}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            {...inputLabelProps}
            placeholder={placeholder}
            fullWidth
            error={!!error}
          />
        )}
        inputValue={search}
        onInputChange={handleInputChange}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={value || null}
        loading={loading}
        loadingText="Searching..."
        noOptionsText="No results found"
        clearOnBlur={false}
        clearOnEscape={false}
        handleHomeEndKeys
        filterOptions={(x) => x}
        getOptionDisabled={getOptionDisabled}
      />
      <ErrorDisplay
        sx={{ ml: 0, my: 1, fontStyle: "italic" }}
        error={error}
        helperText={helperText}
        hideErrors={hideError}
      />
    </>
  );
}
